/**
 * V70.24: 직원 추가 모달용 프로필 사진 선택기
 *
 * **역할은 "사진 선택 + 로컬 preview" 까지만.**
 * - 이 컴포넌트는 **절대로 Storage 에 업로드하지 않는다.**
 * - 실제 업로드는 직원 생성 성공 후 부모(HrEmployeeFormModal)가
 *   기존 `uploadEmployeePhoto` callable 로 수행한다.
 *
 * 파일 정책은 기존 EmployeePhotoSection / uploadEmployeePhoto 와 **동일**하다.
 * (신규 제한을 만들지 않는다)
 * - 허용 MIME: image/jpeg, image/jpg, image/png, image/gif, image/webp
 * - 최대 5MB
 *
 * 표시 규격도 기존 EmployeePhotoSection 을 그대로 따른다.
 * - 3:4 비율 120×160, objectFit: cover, objectPosition: center top
 * - 사진 없을 때 User placeholder
 */

import React, { useRef } from 'react';
import { Camera, Trash2, User } from 'lucide-react';

// 기존 EmployeePhotoSection 과 동일한 규격
const PHOTO_WIDTH = 120;
const PHOTO_HEIGHT = 160; // 3:4

/** 기존 uploadEmployeePhoto 서버 화이트리스트와 동일 */
export const ALLOWED_PHOTO_CONTENT_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
];

/** 기존 정책과 동일: 5MB */
export const MAX_PHOTO_FILE_SIZE = 5 * 1024 * 1024;

interface EmployeePhotoPickerProps {
  /** 선택된 파일 (없으면 null) */
  file: File | null;
  /** 로컬 preview data URL (없으면 null) */
  previewUrl: string | null;
  /** 파일 선택/해제 콜백 — 유효성 검사를 통과한 경우에만 file 이 전달된다 */
  onChange: (file: File | null) => void;
  /** 제출 중 등 비활성화 */
  disabled?: boolean;
}

export function EmployeePhotoPicker({
  file,
  previewUrl,
  onChange,
  disabled = false,
}: EmployeePhotoPickerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePickClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    // 같은 파일 재선택이 가능하도록 항상 초기화
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!selected) return;

    // 기존 EmployeePhotoSection 과 동일한 사전 검증
    if (!selected.type.startsWith('image/')) {
      alert('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    if (!ALLOWED_PHOTO_CONTENT_TYPES.includes(selected.type.toLowerCase())) {
      alert(`허용되지 않는 파일 형식입니다. 허용 형식: ${ALLOWED_PHOTO_CONTENT_TYPES.join(', ')}`);
      return;
    }
    if (selected.size > MAX_PHOTO_FILE_SIZE) {
      alert('파일 크기는 5MB 이하여야 합니다.');
      return;
    }

    onChange(selected);
  };

  const handleRemove = () => {
    if (disabled) return;
    onChange(null);
  };

  return (
    <div style={containerStyle}>
      <div
        style={{ ...photoContainerStyle, cursor: disabled ? 'default' : 'pointer' }}
        onClick={handlePickClick}
        title={disabled ? undefined : '클릭하여 사진 선택'}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="프로필 사진 미리보기" style={photoStyle} />
        ) : (
          <div style={placeholderStyle}>
            <User size={48} color="var(--color-gray-400)" />
          </div>
        )}
      </div>

      <div style={buttonContainerStyle}>
        <button type="button" onClick={handlePickClick} disabled={disabled} style={addButtonStyle}>
          <Camera size={14} />
          {file ? '사진 변경' : '사진 추가'}
        </button>
        {file && (
          <button type="button" onClick={handleRemove} disabled={disabled} style={deleteButtonStyle}>
            <Trash2 size={14} />
            삭제
          </button>
        )}
      </div>

      <div style={hintStyle}>JPG / PNG / GIF / WEBP · 5MB 이하 (선택)</div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_PHOTO_CONTENT_TYPES.join(',')}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />
    </div>
  );
}

// ============================================
// 스타일 (기존 EmployeePhotoSection 규격 재사용)
// ============================================

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
};

const photoContainerStyle: React.CSSProperties = {
  position: 'relative',
  width: PHOTO_WIDTH,
  height: PHOTO_HEIGHT,
  borderRadius: '8px',
  overflow: 'hidden',
  border: '2px solid var(--color-gray-200)',
  background: 'var(--color-gray-100)',
};

const photoStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center top',
};

const placeholderStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--color-gray-100)',
};

const buttonContainerStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
};

const addButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: 500,
  background: 'var(--color-primary)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const deleteButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: 500,
  background: 'white',
  color: 'var(--color-error)',
  border: '1px solid var(--color-error)',
  borderRadius: '6px',
  cursor: 'pointer',
};

const hintStyle: React.CSSProperties = {
  fontSize: '11px',
  color: 'var(--color-gray-500)',
};
